/**
 * The last few seconds of the run, kept so a crash can be played back.
 *
 * Poses only: the recorder never holds an `Aircraft`, because those are
 * mutated in place by the simulation and a stored reference would "record"
 * whatever the object looks like later. `sampleReplay` rebuilds throwaway
 * aircraft for the renderer, which reads them and nothing else.
 */

import type { Aircraft } from "../aircraft/Aircraft";
import { AIRCRAFT_SPECS, type AircraftKind } from "../aircraft/AircraftCatalog";
import type { AircraftState } from "../aircraft/AircraftState";
import { type AirframeKey, AIRFRAMES } from "../aircraft/Airframes";
import { shortestAngleDelta } from "../simulation/geometry";

export interface ReplayPose {
  readonly id: number;
  readonly kind: AircraftKind;
  /** Recorded, not re-derived: the model and its size have to match the run. */
  readonly airframe: AirframeKey;
  readonly state: AircraftState;
  readonly x: number;
  readonly y: number;
  readonly heading: number;
  readonly landProgress: number;
  readonly takeoffProgress: number;
}

export interface ReplayFrame {
  /** Simulation clock, in seconds. */
  readonly time: number;
  readonly poses: readonly ReplayPose[];
}

function poseOf(aircraft: Aircraft): ReplayPose {
  return {
    id: aircraft.id,
    kind: aircraft.kind,
    airframe: aircraft.airframe.key,
    state: aircraft.state,
    x: aircraft.pos.x,
    y: aircraft.pos.y,
    heading: aircraft.heading,
    landProgress: aircraft.landProgress,
    takeoffProgress: aircraft.takeoffProgress,
  };
}

export class ReplayRecorder {
  private frames: ReplayFrame[] = [];
  private nextSampleAt = 0;

  constructor(
    private readonly windowSeconds: number,
    private readonly sampleSeconds: number,
  ) {}

  /** Frames covering the recorded window, oldest first. */
  get window(): readonly ReplayFrame[] {
    return this.frames;
  }

  reset(): void {
    this.frames = [];
    this.nextSampleAt = 0;
  }

  /**
   * Sample the board. Called every simulation step; most calls return
   * immediately, `force` is for the frame a run ends on.
   */
  record(time: number, aircraft: readonly Aircraft[], force = false): void {
    if (!force && time < this.nextSampleAt) {
      return;
    }
    this.nextSampleAt = time + this.sampleSeconds;
    this.frames.push({ time, poses: aircraft.map(poseOf) });

    // Keep the last frame that starts before the window as well, so a sample
    // taken exactly on the boundary still has something to interpolate from.
    const cutoff = time - this.windowSeconds;
    let drop = 0;
    while (
      drop + 1 < this.frames.length &&
      this.frames[drop + 1]!.time < cutoff
    ) {
      drop += 1;
    }
    if (drop > 0) {
      this.frames.splice(0, drop);
    }
  }
}

/** Seconds of footage in a recording. */
export function replayDuration(frames: readonly ReplayFrame[]): number {
  if (frames.length < 2) {
    return 0;
  }
  return frames[frames.length - 1]!.time - frames[0]!.time;
}

function toAircraft(
  pose: ReplayPose,
  x: number,
  y: number,
  heading: number,
): Aircraft {
  return {
    id: pose.id,
    kind: pose.kind,
    spec: AIRCRAFT_SPECS[pose.kind],
    airframe: AIRFRAMES[pose.airframe],
    pos: { x, y },
    heading,
    path: [],
    state: pose.state,
    target: null,
    landProgress: pose.landProgress,
    takeoffProgress: pose.takeoffProgress,
    departureReadyAt: null,
    scheduledDepartureAt: null,
    departureQueueOrder: null,
    age: 0,
    enteredView: true,
  };
}

/**
 * The board as it looked at `time`, interpolated between the two frames that
 * bracket it. Aircraft missing from either side are dropped rather than
 * popped in and out.
 */
export function sampleReplay(
  frames: readonly ReplayFrame[],
  time: number,
): Aircraft[] {
  if (frames.length === 0) {
    return [];
  }

  let index = 0;
  while (index + 1 < frames.length && frames[index + 1]!.time <= time) {
    index += 1;
  }

  const from = frames[index]!;
  const to = frames[index + 1];
  if (!to) {
    return from.poses.map((pose) =>
      toAircraft(pose, pose.x, pose.y, pose.heading),
    );
  }

  const span = to.time - from.time;
  const t =
    span > 1e-6 ? Math.max(0, Math.min(1, (time - from.time) / span)) : 0;
  const next = new Map(to.poses.map((pose) => [pose.id, pose]));

  const cast: Aircraft[] = [];
  for (const pose of from.poses) {
    const later = next.get(pose.id);
    if (!later) {
      continue;
    }
    cast.push(
      toAircraft(
        later,
        pose.x + (later.x - pose.x) * t,
        pose.y + (later.y - pose.y) * t,
        pose.heading + shortestAngleDelta(pose.heading, later.heading) * t,
      ),
    );
  }
  return cast;
}
